/**
 * State Persistence Utility
 * Provides functions to persist and restore Vuex state using localStorage and sessionStorage
 */

// Storage keys
const STORAGE_KEYS = {
  CART: 'lms_cart',
  SELECTED_LOCATION: 'lms_selected_location',
  CHECKOUT_FORM: 'lms_checkout_form',
  USER_SESSION: 'lms_user_session'
}

// Storage expiry (in milliseconds)
const EXPIRY_TIMES = {
  SESSION: 2 * 60 * 60 * 1000, // 2 hours for session data
  FORM_DATA: 24 * 60 * 60 * 1000, // 24 hours for form data
  CART: 7 * 24 * 60 * 60 * 1000 // 7 days for cart data
}

/**
 * Creates a storage item with expiry timestamp
 */
function createStorageItem(data, expiryTime = null) {
  return {
    data,
    timestamp: Date.now(),
    expiry: expiryTime ? Date.now() + expiryTime : null
  }
}

/**
 * Checks if a storage item has expired
 */
function isExpired(storageItem) {
  if (!storageItem || !storageItem.expiry) return false
  return Date.now() > storageItem.expiry
}

/**
 * Storage utility functions
 */
export const StorageUtils = {
  /**
   * Save data to localStorage with expiry
   */
  setLocal(key, data, expiryTime = null) {
    try {
      const item = createStorageItem(data, expiryTime)
      localStorage.setItem(key, JSON.stringify(item))
      return true
    } catch (error) {
      console.warn('Failed to save to localStorage:', error)
      return false
    }
  },

  /**
   * Get data from localStorage with expiry check
   */
  getLocal(key) {
    try {
      const item = localStorage.getItem(key)
      if (!item) return null

      const parsedItem = JSON.parse(item)
      
      if (isExpired(parsedItem)) {
        localStorage.removeItem(key)
        return null
      }

      return parsedItem.data
    } catch (error) {
      console.warn('Failed to get from localStorage:', error)
      return null
    }
  },

  /**
   * Save data to sessionStorage with expiry
   */
  setSession(key, data, expiryTime = null) {
    try {
      const item = createStorageItem(data, expiryTime)
      sessionStorage.setItem(key, JSON.stringify(item))
      return true
    } catch (error) {
      console.warn('Failed to save to sessionStorage:', error)
      return false
    }
  },

  /**
   * Get data from sessionStorage with expiry check
   */
  getSession(key) {
    try {
      const item = sessionStorage.getItem(key)
      if (!item) return null

      const parsedItem = JSON.parse(item)
      
      if (isExpired(parsedItem)) {
        sessionStorage.removeItem(key)
        return null
      }

      return parsedItem.data
    } catch (error) {
      console.warn('Failed to get from sessionStorage:', error)
      return null
    }
  },

  /**
   * Remove item from both storages
   */
  remove(key) {
    try {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    } catch (error) {
      console.warn('Failed to remove from storage:', error)
    }
  },

  /**
   * Clear expired items from both storages
   */
  clearExpired() {
    try {
      // Clear expired localStorage items
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)
        if (key && key.startsWith('lms_')) {
          const item = this.getLocal(key) // This will remove expired items
        }
      }

      // Clear expired sessionStorage items
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i)
        if (key && key.startsWith('lms_')) {
          const item = this.getSession(key) // This will remove expired items
        }
      }
    } catch (error) {
      console.warn('Failed to clear expired items:', error)
    }
  }
}

/**
 * State persistence functions for specific modules
 */
export const StatePersistence = {
  // Cart persistence (using localStorage for longer retention)
  saveCart(cartData) {
    return StorageUtils.setLocal(STORAGE_KEYS.CART, cartData, EXPIRY_TIMES.CART)
  },

  getCart() {
    return StorageUtils.getLocal(STORAGE_KEYS.CART) || []
  },

  // Location persistence (using sessionStorage for current session)
  saveSelectedLocation(location) {
    return StorageUtils.setSession(STORAGE_KEYS.SELECTED_LOCATION, location, EXPIRY_TIMES.SESSION)
  },

  getSelectedLocation() {
    return StorageUtils.getSession(STORAGE_KEYS.SELECTED_LOCATION)
  },

  // Checkout form persistence (using localStorage with longer expiry)
  saveCheckoutForm(formData) {
    return StorageUtils.setLocal(STORAGE_KEYS.CHECKOUT_FORM, formData, EXPIRY_TIMES.FORM_DATA)
  },

  getCheckoutForm() {
    return StorageUtils.getLocal(STORAGE_KEYS.CHECKOUT_FORM)
  },

  // User session data (temporary data for current session)
  saveUserSession(sessionData) {
    return StorageUtils.setSession(STORAGE_KEYS.USER_SESSION, sessionData, EXPIRY_TIMES.SESSION)
  },

  getUserSession() {
    return StorageUtils.getSession(STORAGE_KEYS.USER_SESSION)
  },

  // Remove specific key from storage
  remove(key) {
    return StorageUtils.remove(key)
  },

  // Clear all persisted data
  clearAll() {
    Object.values(STORAGE_KEYS).forEach(key => {
      StorageUtils.remove(key)
    })
  },

  // Initialize - clear expired data on app start
  initialize() {
    StorageUtils.clearExpired()
  }
}

// Export storage keys for direct access if needed
export { STORAGE_KEYS, EXPIRY_TIMES }